import { useMutation } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { EntityPageCoverMediaType } from '@xing-com/crate-common-graphql-types';
import type { ImageUploadOnPublishData } from '@xing-com/crate-entity-pages-common';
import {
  ExternalEditInfoBanner,
  ImageUpload,
  NotSavedDialog,
  SmallGrid,
} from '@xing-com/crate-entity-pages-common';
import {
  trackEditOpening,
  trackEditSaving,
  trackDelete,
} from '@xing-com/crate-entity-pages-common/src/tracking';
import {
  DialogContextProvider,
  useDialogContext,
} from '@xing-com/crate-entity-pages-entity-pages-shared-context-dialog-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import type { ErrorType } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { useErrorContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { useFeatureSwitch } from '@xing-com/hub';
import { IconImage, IconUpload, IconTrash } from '@xing-com/icons';
import { Menu } from '@xing-com/menu';
import { ModalPage } from '@xing-com/modal-page';
import { usePopOver } from '@xing-com/pop-over';

import { EntityPageDeleteCoverDocument } from '../../../graphql/mutations/delete-cover.gql-types';
import { EntityPageUpdateCoverDocument } from '../../../graphql/mutations/update-cover.gql-types';

import * as Styled from './edit-cover.styles';
import { UpdateCover } from './update-cover/update-cover';
import { UpsellBannerCompanyHeaderBanner } from './upsell-banner/upsell-banner-company-header-banner';
import { UpsellBannerContainer } from './upsell-banner/upsell-banner-container';
import { UpsellBannerUploadImage } from './upsell-banner/upsell-banner-upload-image';

export const ENTITY_PAGES_UNFENCE_HEADER = 'entity_pages_unfence_header';

const EditCoverMenuInner: FC = () => {
  const { $t } = useIntl();
  const popOverMenu = usePopOver();
  const { showError } = useErrorContext();
  const { pageContext, setCoverImage } = usePageContext();
  const {
    setDataChanged,
    dataChanged,
    dialogConfirmation,
    setDialogConfirmation,
  } = useDialogContext();

  const [isEditCrop, setIsEditCrop] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModalCover, setShowModalCover] = useState(false);

  const { coverImage, isFreePage, isCompanyPage } = pageContext;

  const hasCover = coverImage && coverImage.length > 0;
  const hasFenceFS = useFeatureSwitch(ENTITY_PAGES_UNFENCE_HEADER, false);
  const isFreeHeaderProfile = hasFenceFS && isFreePage && isCompanyPage;

  const displayError = (error: ErrorType) => {
    showError({ message: 'EP_GENERAL_FEEDBACK_ERROR', error });
  };

  const [updateCover] = useMutation(EntityPageUpdateCoverDocument);
  const [deleteCover] = useMutation(EntityPageDeleteCoverDocument);

  const handleShowModalCover = () => setShowModalCover(true);
  const handleHideModalCover = () => setShowModalCover(false);
  const handleShowDialog = () => setShowDialog(true);
  const handleHideDialog = () => setShowDialog(false);

  const handleClickMenuItem = (isEditCrop = false) => {
    trackEditOpening({
      isFreePage: isFreeHeaderProfile,
      focusType: pageContext.focusType,
      itemId: pageContext.pageId,
      module: 'header',
      part: 'image',
    });

    setIsEditCrop(isEditCrop);
    popOverMenu.handleHide();
    handleShowModalCover();
  };

  const handleDeleteClick = () => {
    popOverMenu.handleHide();
    handleShowDialog();
  };
  const handleConfirmDelete = async () => {
    setIsLoading(true);

    const { data, errors } = await deleteCover({
      variables: {
        id: pageContext.pageSlug,
      },
    });

    const error = data?.entityPageDeleteCover?.error || errors?.[0];
    if (error) {
      displayError(error);
    } else {
      if (isFreeHeaderProfile) {
        trackDelete({
          focusType: pageContext.focusType,
          itemId: pageContext.pageId,
          isFreePage: !!pageContext.isFreePage,
        });
      }

      setCoverImage();
    }

    setIsLoading(false);
    handleHideDialog();
  };

  const handleDiscard = () => {
    if (!dataChanged) {
      handleHideModalCover();
      return;
    }

    setDialogConfirmation({
      dialogAction: () => {
        setDialogConfirmation(null);
        setDataChanged(false);
        handleHideModalCover();
      },
    });
  };

  const handleOnUpload = async ({
    cropped,
    original,
  }: ImageUploadOnPublishData) => {
    const { data, errors } = await updateCover({
      variables: {
        id: pageContext.pageSlug,
        coverMediaType: EntityPageCoverMediaType.Image,
        coverMediaUploadId: cropped.id,
        originalCoverMediaUploadId: original?.id,
      },
    });

    const error = data?.entityPageUpdateCover?.error || errors?.[0];
    if (error) {
      displayError(error);
      return;
    }

    setCoverImage(URL.createObjectURL(cropped.file));
    setDataChanged(false);
    handleHideModalCover();
  };
  const handleOnUpdateCover = ({
    cropped,
    original,
  }: ImageUploadOnPublishData) => {
    trackEditSaving({
      focusType: pageContext.focusType,
      itemId: pageContext.pageId,
      module: 'header',
      part: 'image',
    });
    handleOnUpload({ cropped, original });
  };

  const addMobileGuideline = () => {
    const mobileGuideline = document.getElementById('mobileGuideline');
    if (!mobileGuideline) {
      const node = document.createElement('div');
      node.id = 'mobileGuideline';
      node.className = 'mobileGuidelineContainer';
      const x = document.getElementsByClassName('reactEasyCrop_CropArea')[0];

      x.appendChild(node);
    }
  };

  return (
    <>
      <Styled.GlobalEditMenuStyles />
      <Styled.Dialog
        show={showDialog}
        data-testid="DELETE_DIALOG"
        isDestructive={true}
        loading={isLoading}
        headline={$t({ id: 'EP_DELETE_COVER_DIALOG_HEADLINE' })}
        text={$t({ id: 'EP_DELETE_COVER_DIALOG' })}
        cancelLabel={$t({ id: 'EP_DELETE_COVER_DIALOG_KEEP' })}
        confirmLabel={$t({ id: 'EP_DELETE_COVER_DIALOG_CONFIRM' })}
        onCancel={handleHideDialog}
        onConfirm={handleConfirmDelete}
      />
      <Styled.CoverEditButton
        size="small"
        showIcon
        data-testid="EDIT_BUTTON_ON_COVER_IMAGE"
        onClick={popOverMenu.handleShow}
        innerRef={popOverMenu.triggerRef}
      />

      <Menu
        dimmerTitle="close menu"
        onOutsideClick={popOverMenu.handleHide}
        triggerRef={popOverMenu.triggerRef}
        show={popOverMenu.show}
      >
        <Styled.ListWrapper>
          <Styled.ListItem onClick={() => handleClickMenuItem(false)}>
            <IconUpload width={24} height={24} />
            <Styled.Link noMargin size="small">
              <FormattedMessage id="EP_UPLOAD_COVER" />
            </Styled.Link>
          </Styled.ListItem>
          {hasCover ? (
            <>
              <Styled.ListItem onClick={() => handleClickMenuItem(true)}>
                <IconImage width={24} height={24} />
                <Styled.Link noMargin size="small">
                  <FormattedMessage id="EP_EDIT_SECTION" />
                </Styled.Link>
              </Styled.ListItem>
              <Styled.ListItem onClick={handleDeleteClick}>
                <IconTrash width={24} height={24} />
                <Styled.Link noMargin size="small">
                  <FormattedMessage id="EP_DELETE_COVER" />
                </Styled.Link>
              </Styled.ListItem>
            </>
          ) : null}
        </Styled.ListWrapper>
      </Menu>

      <ModalPage
        isShown={showModalCover}
        closeButton={{ onClick: handleHideModalCover }}
        isAnimated
        noGrid
      >
        <SmallGrid>
          {isFreeHeaderProfile ? (
            <UpsellBannerContainer isEditCrop={isEditCrop}>
              <UpsellBannerUploadImage
                onDiscard={handleDiscard}
                onCancel={handleHideModalCover}
                onDragCrop={() => setDataChanged(true)}
                onCropReady={() => addMobileGuideline()}
              />
              <UpsellBannerCompanyHeaderBanner onClickBanner={handleOnUpload} />
            </UpsellBannerContainer>
          ) : null}
          {!pageContext.isFreePage ? (
            <>
              {isEditCrop ? (
                <UpdateCover
                  aspectRatio={8 / 2}
                  isCover={true}
                  descriptionCopyKey="EP_COVER_CROP_DESCRIPTION"
                  onPublish={handleOnUpdateCover}
                  onCancel={handleHideModalCover}
                  onDiscard={() => handleDiscard()}
                  onDragCrop={() => setDataChanged(true)}
                  onCropReady={() => addMobileGuideline()}
                />
              ) : (
                <ImageUpload
                  aspectRatio={8 / 2}
                  shouldUploadOriginal={true}
                  onCropReady={() => addMobileGuideline()}
                  onDiscard={() => {
                    if (dataChanged) handleDiscard();
                    else setShowModalCover(false);
                  }}
                  onFileSelected={() => setDataChanged(true)}
                  onPublish={handleOnUpdateCover}
                  descriptionCopyKey="EP_COVER_CROP_DESCRIPTION"
                  imageUploadCopy={{
                    headline: $t({ id: 'EP_CHOOSE_COVER' }),
                    hint: $t({ id: 'EP_UPLOAD_IMAGE_HINT' }),
                    label: $t({ id: 'EP_UPLOAD_COVER_LABEL' }),
                    onDragLabel: $t({ id: 'EP_UPLOAD_IMAGE_DRAG_LABEL' }),
                  }}
                />
              )}
              <ExternalEditInfoBanner
                copyKey="EP_CHOOSE_COVER_INFO_BANNER"
                ctaUrl={`https://help.onlyfy.com/hc/de/articles/8803872010001-Erstbef%C3%BCllung-des-Employer-Branding-Profils-auf-XING#titel--/-headerbild`}
                ctaCopyKey="EP_CHOOSE_COVER_INFO_BANNER_CTA"
              />
            </>
          ) : null}
        </SmallGrid>
      </ModalPage>

      {dataChanged && dialogConfirmation?.dialogAction ? (
        <NotSavedDialog />
      ) : null}
    </>
  );
};

export const EditCoverMenu: FC = () => (
  <DialogContextProvider>
    <EditCoverMenuInner />
  </DialogContextProvider>
);
